import React, { useEffect, useRef } from "react";

import { ArrowRight, CheckCircle2, Circle, Loader2, X } from "lucide-react";

import type { BriefingCoverage, PlanQueueLane } from "../../Types/AccountPlan";
import { laneLine } from "../../Utils/AccountPlans/queueText";

export type GenerateState = "running" | "ready" | "failed";

interface Props {
  /** The plan number people see (D1), not the internal upload number. */
  version: number | null;
  filename: string;
  slides: number | null | undefined;
  state: GenerateState;
  /** Shown when generation fails. */
  error?: string | null;
  /** Briefing questions answered in the same pass (0 hides the step). */
  questions?: number;
  sponsorName?: string | null;
  /** Coverage of those answers once they exist. */
  coverage?: BriefingCoverage | null;
  /** The briefing lane's place in line while it waits or runs (spec QUE-03). */
  queue?: PlanQueueLane | null;
  onClose: () => void;
  onReview: () => void;
}

/**
 * Draft briefing progress (shown on "Try again" / Regenerate; the first briefing
 * starts on Confirm plan): one pass builds the nine
 * sections. The stages mirror what the server really does; the middle one
 * spins until the briefing is ready, because the server does it in one call.
 */
const GenerateDraftModal: React.FC<Props> = ({
  version,
  filename,
  slides,
  state,
  error,
  questions = 0,
  sponsorName,
  coverage,
  queue,
  onClose,
  onReview,
}) => {
  const dialog = useRef<HTMLDivElement>(null);
  useEffect(() => {
    dialog.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const done = state === "ready";
  const waiting = state === "running" && queue?.state === "queued";
  const working = done
    ? "done"
    : state === "running" && !waiting
      ? "active"
      : "pending";
  const whose = sponsorName ? `${sponsorName}'s` : "the sponsor's";
  type Stage = { label: string; status: "done" | "active" | "pending" };
  const answering: Stage[] = questions
    ? [
        {
          label: `Answering ${whose} ${questions} briefing questions, each cited to slides`,
          status: working,
        },
      ]
    : [];
  const checkingCoverage: Stage[] = questions
    ? [
        {
          label:
            done && coverage
              ? `Checking coverage — ${coverage.answered} answered · ${coverage.partial} partial · ${coverage.missing} missing from the plan`
              : "Checking coverage against the plan",
          status: done ? "done" : "pending",
        },
      ]
    : [];
  const inLine: Stage[] = waiting
    ? [{ label: laneLine(queue, "brief"), status: "active" }]
    : [];
  const stages: Stage[] = [
    {
      label: `Reading plan${version != null ? ` v${version}` : ""}${slides ? ` — ${slides} slides` : ""}`,
      status: "done",
    },
    ...inLine,
    {
      label:
        "Building the 9 briefing sections — priorities, stakeholders, renewal, risks, sponsor asks",
      status: working,
    },
    ...answering,
    {
      label: "Checking every quote against its slide",
      status: done ? "done" : "pending",
    },
    ...checkingCoverage,
    {
      label: "Starting review — the AE and CSM verify their sections",
      status: done ? "done" : "pending",
    },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 p-4"
      onMouseDown={e => e.target === e.currentTarget && onClose()}
    >
      <div
        ref={dialog}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="generate-title"
        className="w-full max-w-lg rounded-xl bg-white p-5 shadow-xl outline-none"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-gray-400">
              Writing the draft briefing
            </div>
            <h2
              id="generate-title"
              className="mt-1 text-lg font-semibold text-gray-900"
            >
              Draft briefing from plan{version != null ? ` v${version}` : ""}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close dialog"
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="mt-2 text-sm text-gray-600">
          One pass over <span className="font-mono text-xs">{filename}</span>{" "}
          builds the briefing the sponsor reads
          {questions ? " and the answers to their briefing questions" : ""}.
        </p>
        <ol
          className="mt-3 space-y-2 rounded-lg border border-gray-100 bg-gray-50 p-3"
          aria-live="polite"
        >
          {stages.map(s => (
            <li key={s.label} className="flex items-start gap-2 text-sm">
              {s.status === "done" ? (
                <CheckCircle2
                  className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600"
                  aria-label="done"
                />
              ) : s.status === "active" ? (
                <Loader2
                  className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-[#974ae4]"
                  aria-label="in progress"
                />
              ) : (
                <Circle
                  className="mt-0.5 h-4 w-4 shrink-0 text-gray-300"
                  aria-label="pending"
                />
              )}
              <span
                className={
                  s.status === "pending"
                    ? "text-gray-400"
                    : s.status === "active"
                      ? "font-medium text-purple-800"
                      : "text-gray-800"
                }
              >
                {s.label}
              </span>
            </li>
          ))}
        </ol>
        {state === "running" && (
          <p className="mt-3 text-xs text-gray-500" data-testid="generate-eta">
            {queue && !waiting ? `${laneLine(queue, "brief")}. ` : ""}
            You can close this — the briefing keeps going and you&apos;ll be
            notified when it&apos;s ready.
          </p>
        )}
        {state === "ready" && (
          <p className="mt-3 text-sm text-gray-700">
            <span className="font-semibold text-emerald-700">Draft ready.</span>{" "}
            The sponsor now sees it marked Under review. You verify your
            sections, the CSM confirms theirs, then you publish.
          </p>
        )}
        {state === "failed" && (
          <p role="alert" className="mt-3 text-sm text-red-600">
            {error || "The draft briefing couldn't be generated. Try again."}
          </p>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 bg-white px-3.5 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50"
          >
            {state === "ready"
              ? "Stay here"
              : state === "running"
                ? "Close, notify me"
                : "Close"}
          </button>
          {state === "ready" && (
            <button
              type="button"
              onClick={onReview}
              className="inline-flex items-center gap-1.5 rounded-lg bg-[#974ae4] px-3.5 py-2 text-sm font-semibold text-white hover:bg-[#8538d4] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50"
            >
              <ArrowRight className="h-4 w-4" aria-hidden />
              Review the draft
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default GenerateDraftModal;
